//requests
import { getOfficeProfiles } from "@/requests/administration/administrationOffices";
import {
  assignBankAccountToOfficeProfile,
  disconnectBankAccountToOfficeProfile,
  setDefaultBankAccountToOfficeProfile,
  unsetDefaultBankAccountToOfficeProfile,
} from "@/requests/bankAccounts";
import { getBankAccountAssign } from "@/requests/bankAccounts";

//vue
import { defineModule } from "direct-vuex";
import { DataOptions } from "vuetify";
import { moduleActionContext } from "@/store";

//models
import { OfficeProfileResult } from "@/models/api/administration/servicesAndAttributes/ServiceTypeExportConfiguration";
import {
  BankAccount,
  BankAccountResponse,
  BankAccountOfficeProfileRequest,
} from "@/models/api/administration/bankAccounts/BankAccountsList";

//others
import _ from "lodash";
import { FILTER_NAMES } from "@/static/filterNames";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";

export interface AdministrationBankAccountsAssignStateInterface {
  //Filter
  bankAccountsFilters: null | Array<CriterionBase>;

  //Table bank accounts
  bankAccountsDataTableOptions: DataOptions;
  bankAccounts: Array<BankAccount>;
  bankAccountsCount: number;

  //Table office profiles
  officeProfiles: Array<OfficeProfileResult>;
  officeProfilesCount: number;
  officeProfileSelected: OfficeProfileResult;
  officeProfileSelectedId: number;
}

export const AdministrationBankAccountsAssignState: AdministrationBankAccountsAssignStateInterface = {
  //Filter
  bankAccountsFilters: null,

  //Table bank accounts
  bankAccountsDataTableOptions: new DefaultDataOptions(),
  bankAccounts: [],
  bankAccountsCount: -1,

  //Table office profiles
  officeProfiles: [],
  officeProfilesCount: -1,
  officeProfileSelected: new OfficeProfileResult(),
  officeProfileSelectedId: -1,
};

const AdministrationBankAccountsAssignModule = defineModule({
  state: AdministrationBankAccountsAssignState as AdministrationBankAccountsAssignStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setBankAccountFilters(state, param: Array<CriterionBase>) {
      state.bankAccountsFilters = param;
    },
    setBankAccountsDataTableOptions(state, param: DataOptions) {
      state.bankAccountsDataTableOptions = param;
    },
    setBankAccounts(state, param: BankAccountResponse) {
      state.bankAccounts = param.items;
      state.bankAccountsCount = param.totalCount;
    },

    //Office profiles
    setOfficeProfile(state, param: Array<OfficeProfileResult>) {
      state.officeProfiles = param;
      state.officeProfilesCount = param.length;
    },
    setSelectedOfficeProfile(state, param: OfficeProfileResult) {
      state.officeProfileSelected = param;
      state.officeProfileSelectedId = param.id;
      state.bankAccountsDataTableOptions.page = 0;
    },
  },
  actions: {
    async downloadOffices(context) {
      const { commit } = _moduleActionContext(context);
      const offices = await getOfficeProfiles();
      commit.setOfficeProfile(offices);
    },
    async downloadBankAccountAssignedTo(context) {
      const { state, commit } = _moduleActionContext(context);
      const officeProfilesId = state.officeProfileSelectedId;
      const bankAccountsAssigned = await getBankAccountAssign(officeProfilesId, {
        count: state.bankAccountsDataTableOptions.itemsPerPage,
        page: state.bankAccountsDataTableOptions.page - 1,
        criterions: _.cloneDeep(state.bankAccountsFilters),
        filterName: FILTER_NAMES.BankAccountAssignFilter,
      });
      commit.setBankAccounts(bankAccountsAssigned);
    },
    async assign(context, bankAccount: BankAccount) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      const request = new BankAccountOfficeProfileRequest();
      request.bankAccountId = bankAccount.id;
      request.officeProfileId = state.officeProfileSelectedId;
      await assignBankAccountToOfficeProfile(request);
      dispatch.downloadBankAccountAssignedTo();
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async disconnect(context, bankAccount: BankAccount) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      const request = new BankAccountOfficeProfileRequest();
      request.bankAccountId = bankAccount.id;
      request.officeProfileId = state.officeProfileSelectedId;
      await disconnectBankAccountToOfficeProfile(request);
      dispatch.downloadBankAccountAssignedTo();
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async setDefault(context, bankAccount: BankAccount) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      const request = new BankAccountOfficeProfileRequest();
      request.bankAccountId = bankAccount.id;
      request.officeProfileId = state.officeProfileSelectedId;
      await setDefaultBankAccountToOfficeProfile(request);
      dispatch.downloadBankAccountAssignedTo();
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async unsetDefault(context, bankAccount: BankAccount) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      const request = new BankAccountOfficeProfileRequest();
      request.bankAccountId = bankAccount.id;
      request.officeProfileId = state.officeProfileSelectedId;
      await unsetDefaultBankAccountToOfficeProfile(request);
      dispatch.downloadBankAccountAssignedTo();
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
  },
});

export default AdministrationBankAccountsAssignModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationBankAccountsAssignModule);
